import { ArrowDownward, ArrowUpward, InfoOutlined } from '@mui/icons-material';
import {
    Box,
    Button,
    Dialog,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getPropertyVendorsMap } from 'entities/Vendors/model/selectors/vendorsSelectors';
import { getPrice } from 'shared/lib/getPrice/getPrice';
import { DialogBoxContent } from 'shared/ui/DialogBoxContent/DialogBoxContent';
import { SupplierLogo } from 'shared/ui/SupplierLogo/ui/SupplierLogo';

import { OrderRecheckResponse } from '../../model/types/orderRecheckSchema';

interface PriceAvailabilityCheckModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: () => void;
    onReview: () => void;
    recheckData: OrderRecheckResponse;
    isLoading?: boolean;
}

export const PriceAvailabilityCheckModal = memo(
    ({
        open,
        onClose,
        onSubmit,
        onReview,
        recheckData,
        isLoading = false,
    }: PriceAvailabilityCheckModalProps) => {
        const { t } = useTranslation();
        const vendorsMap = useSelector(getPropertyVendorsMap);

        const hasPriceChange = useMemo(() => {
            const delta = Number.parseFloat(recheckData.totalPriceDelta);
            return !Number.isNaN(delta) && delta !== 0;
        }, [recheckData.totalPriceDelta]);

        const hasSupplierChanges = useMemo(() => {
            return (
                (recheckData.suppliersAdded?.length ?? 0) > 0 ||
                (recheckData.suppliersRemoved?.length ?? 0) > 0
            );
        }, [recheckData.suppliersAdded, recheckData.suppliersRemoved]);

        const hasUnavailableItems = useMemo(() => {
            return (
                (recheckData.unavailableSelectedCount ?? 0) > 0 ||
                (recheckData.unavailableSelectedShortageTotal ?? 0) > 0
            );
        }, [
            recheckData.unavailableSelectedCount,
            recheckData.unavailableSelectedShortageTotal,
        ]);

        const hasAvailabilityChanges = useMemo(() => {
            return hasSupplierChanges || hasUnavailableItems;
        }, [hasSupplierChanges, hasUnavailableItems]);

        const priceDelta = useMemo(() => {
            const delta = Number.parseFloat(recheckData.totalPriceDelta);
            return !Number.isNaN(delta) && delta !== 0 ? delta : 0;
        }, [recheckData.totalPriceDelta]);

        const isPriceIncrease = priceDelta > 0;
        const isBlocking = hasUnavailableItems;

        // Determine header and content based on UX content rules
        const { header, content } = useMemo(() => {
            const combinedReturn = {
                header: t('Price & Availability Change'),
                content: t(
                    'We refreshed your order with the latest supplier pricing and availability.',
                ),
            };

            if (hasPriceChange && hasAvailabilityChanges) {
                return combinedReturn;
            } else if (hasPriceChange && !hasAvailabilityChanges) {
                return {
                    header: t('Price Change'),
                    content: t(
                        'We refreshed your order with the latest supplier pricing.',
                    ),
                };
            } else if (hasAvailabilityChanges && !hasPriceChange) {
                return {
                    header: t('Availability Change'),
                    content: t(
                        'We refreshed your order with the latest supplier availability.',
                    ),
                };
            }

            return combinedReturn;
        }, [hasPriceChange, hasAvailabilityChanges, t]);

        // Reusable table cell styles
        const tableHeaderTitleStyle = {
            fontSize: '1.4rem',
            lineHeight: '2.1rem',
            fontWeight: '700',
            border: 'none',
            padding: '0 0 8px 0',
            borderBottom: '1px solid #D9D9D9',
        };

        const tableHeaderCellStyle = {
            fontSize: '1.2rem',
            lineHeight: '1.6rem',
            fontWeight: 600,
            textAlign: 'right' as const,
            border: 'none',
            padding: '5px 0 12px 0',
            borderBottom: '1px solid #E0E0E0',
        };

        const tableBodyCellStyle = {
            fontSize: '1.4rem',
            border: 'none',
            padding: '12px 0 0 0',
        };

        const visibleSections = useMemo(() => {
            const sections: string[] = [];
            if (hasPriceChange) sections.push('price');
            if (hasSupplierChanges) sections.push('suppliers');
            if (hasUnavailableItems) sections.push('stock');
            return sections;
        }, [hasPriceChange, hasSupplierChanges, hasUnavailableItems]);

        const sectionMargin = '50px';
        const lastSectionMargin = visibleSections.length > 1 ? '44px' : '59px'; // 60px - 16px or 75px - 16px

        // Section rendering functions
        const renderPriceSection = () => {
            if (!hasPriceChange) return null;

            const isLastSection =
                visibleSections[visibleSections.length - 1] === 'price';

            return (
                <Box
                    sx={{
                        mb: isLastSection ? lastSectionMargin : sectionMargin,
                    }}
                >
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={tableHeaderTitleStyle}>
                                        {t('Price')}
                                    </TableCell>
                                    <TableCell sx={tableHeaderCellStyle}>
                                        {t('Old')}
                                    </TableCell>
                                    <TableCell sx={tableHeaderCellStyle}>
                                        {t('New')}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            ...tableHeaderCellStyle,
                                            width: '132px',
                                        }}
                                    >
                                        {t('Difference')}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell
                                        sx={{
                                            ...tableBodyCellStyle,
                                            color: 'rgba(0, 0, 0, 0.5)',
                                        }}
                                    >
                                        {t('Estimated total')}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            ...tableBodyCellStyle,
                                            textAlign: 'right',
                                        }}
                                    >
                                        {getPrice(recheckData.totalPriceOld)}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            ...tableBodyCellStyle,
                                            fontWeight: 600,
                                            textAlign: 'right',
                                        }}
                                    >
                                        {getPrice(recheckData.totalPriceNew)}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            ...tableBodyCellStyle,
                                            fontWeight: 600,
                                            textAlign: 'right',
                                            width: '132px',
                                            color: isPriceIncrease
                                                ? '#FF0000'
                                                : '#2CB353',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-end',
                                                gap: '4px',
                                            }}
                                        >
                                            {getPrice(
                                                Math.abs(priceDelta).toString(),
                                            )}
                                            {isPriceIncrease ? (
                                                <ArrowUpward
                                                    sx={{
                                                        fontSize: '1.6rem',
                                                    }}
                                                />
                                            ) : (
                                                <ArrowDownward
                                                    sx={{
                                                        fontSize: '1.6rem',
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            );
        };

        const renderSuppliersSection = () => {
            if (!hasSupplierChanges) return null;

            const isLastSection =
                visibleSections[visibleSections.length - 1] === 'suppliers';

            const hasAddedAndRemovedSuppliers =
                (recheckData.suppliersAdded?.length ?? 0) > 0 &&
                (recheckData.suppliersRemoved?.length ?? 0) > 0;

            return (
                <Box
                    sx={{
                        mb: isLastSection ? lastSectionMargin : sectionMargin,
                    }}
                >
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={tableHeaderTitleStyle}>
                                        {t('Suppliers')}
                                    </TableCell>
                                    <TableCell sx={tableHeaderCellStyle}>
                                        &nbsp;
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {recheckData.suppliersAdded &&
                                    recheckData.suppliersAdded.length > 0 &&
                                    recheckData.suppliersAdded.map(
                                        (supplier, index) => {
                                            const isLastAdded =
                                                hasAddedAndRemovedSuppliers &&
                                                index ===
                                                    recheckData.suppliersAdded
                                                        .length -
                                                        1;
                                            return (
                                                <TableRow key={supplier.uid}>
                                                    <TableCell
                                                        sx={{
                                                            ...tableBodyCellStyle,
                                                            color: 'rgba(0, 0, 0, 0.5)',
                                                            ...(isLastAdded && {
                                                                paddingBottom:
                                                                    '12px',
                                                            }),
                                                        }}
                                                    >
                                                        {index === 0 &&
                                                            t('Added')}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            ...tableBodyCellStyle,
                                                            textAlign: 'right',
                                                            ...(isLastAdded && {
                                                                paddingBottom:
                                                                    '12px',
                                                            }),
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems:
                                                                    'center',
                                                                gap: '10px',
                                                                justifyContent:
                                                                    'flex-end',
                                                            }}
                                                        >
                                                            <SupplierLogo
                                                                vendor={
                                                                    vendorsMap?.[
                                                                        supplier
                                                                            .uid
                                                                    ]
                                                                }
                                                                size={20}
                                                            />
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontSize:
                                                                        '1.4rem',
                                                                }}
                                                            >
                                                                {supplier.name}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        },
                                    )}

                                {recheckData.suppliersRemoved &&
                                    recheckData.suppliersRemoved.length > 0 &&
                                    recheckData.suppliersRemoved.map(
                                        (supplier, index) => (
                                            <TableRow key={supplier.uid}>
                                                <TableCell
                                                    sx={{
                                                        ...tableBodyCellStyle,
                                                        color: 'rgba(0, 0, 0, 0.5)',
                                                        ...(hasAddedAndRemovedSuppliers &&
                                                            index === 0 && {
                                                                borderTop:
                                                                    '1px solid #E0E0E0',
                                                                paddingTop:
                                                                    '12px',
                                                                paddingBottom:
                                                                    '0',
                                                            }),
                                                    }}
                                                >
                                                    {index === 0 &&
                                                        t('Removed')}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        ...tableBodyCellStyle,
                                                        textAlign: 'right',
                                                        ...(hasAddedAndRemovedSuppliers &&
                                                            index === 0 && {
                                                                borderTop:
                                                                    '1px solid #E0E0E0',
                                                                paddingTop:
                                                                    '12px',
                                                                paddingBottom:
                                                                    '0',
                                                            }),
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                            gap: '10px',
                                                            justifyContent:
                                                                'flex-end',
                                                        }}
                                                    >
                                                        <SupplierLogo
                                                            vendor={
                                                                vendorsMap?.[
                                                                    supplier.uid
                                                                ]
                                                            }
                                                            size={20}
                                                        />
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontSize:
                                                                    '1.4rem',
                                                            }}
                                                        >
                                                            {supplier.name}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ),
                                    )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            );
        };

        const renderStockSection = () => {
            if (!hasUnavailableItems) return null;

            const isLastSection =
                visibleSections[visibleSections.length - 1] === 'stock';

            return (
                <Box
                    sx={{
                        mb: isLastSection ? lastSectionMargin : sectionMargin,
                    }}
                >
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={tableHeaderTitleStyle}>
                                        {t('Stock')}
                                    </TableCell>
                                    <TableCell sx={tableHeaderCellStyle}>
                                        {t('Items')}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            ...tableHeaderCellStyle,
                                            width: '132px',
                                        }}
                                    >
                                        {t('Quantities')}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell
                                        sx={{
                                            ...tableBodyCellStyle,
                                            color: 'rgba(0, 0, 0, 0.5)',
                                        }}
                                    >
                                        {t('Unavailable')}
                                    </TableCell>
                                    <TableCell
                                        sx={() => ({
                                            ...tableBodyCellStyle,
                                            fontWeight: 600,
                                            textAlign: 'right',
                                            color: '#F00',
                                        })}
                                    >
                                        {recheckData.unavailableSelectedCount}
                                    </TableCell>
                                    <TableCell
                                        sx={() => ({
                                            ...tableBodyCellStyle,
                                            fontWeight: 600,
                                            textAlign: 'right',
                                            width: '132px',
                                            color: '#F00',
                                        })}
                                    >
                                        {
                                            recheckData.unavailableSelectedShortageTotal
                                        }
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginTop: '40px',
                        }}
                    >
                        <InfoOutlined
                            sx={{
                                fontSize: '2rem',
                                color: '#FF0000',
                                transform: 'rotate(180deg)',
                            }}
                        />
                        <Typography variant="body2" sx={{ fontSize: '1.4rem' }}>
                            {t(
                                'Update quantities to match current availability to submit.',
                            )}
                        </Typography>
                    </Box>
                </Box>
            );
        };

        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth={false}
                PaperProps={{
                    sx: {
                        width: '602px',
                        maxWidth: '602px',
                    },
                }}
            >
                <DialogBoxContent
                    onClose={onClose}
                    buttons={
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                width: '100%',
                                gap: '10px',
                            }}
                        >
                            <Button
                                onClick={onReview}
                                disabled={isLoading}
                                sx={{
                                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                    color: '#6b6b6b',
                                    fontSize: '1.5rem',
                                    fontWeight: 600,
                                    lineHeight: '2.6rem',
                                    padding: '8px 20px',
                                    borderRadius: '4px',
                                    textTransform: 'none',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.12)',
                                    },
                                    '&:disabled': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                        color: '#6b6b6b',
                                        opacity: 0.6,
                                    },
                                }}
                            >
                                {t('Review Order')}
                            </Button>
                            {!isBlocking && (
                                <Button
                                    onClick={onSubmit}
                                    variant="contained"
                                    color="primary"
                                    disabled={isLoading}
                                >
                                    {t('Submit Order')}
                                </Button>
                            )}
                        </Box>
                    }
                >
                    <Box sx={{ width: '100%' }}>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                mb: '30px',
                                fontSize: '1.6rem',
                                fontWeight: 'bold',
                            }}
                        >
                            {header}
                        </Typography>

                        <Typography
                            variant="body2"
                            sx={{
                                mb: '40px',
                                fontSize: '1.4rem',
                            }}
                        >
                            {content}
                        </Typography>

                        {renderPriceSection()}
                        {renderSuppliersSection()}
                        {renderStockSection()}
                    </Box>
                </DialogBoxContent>
            </Dialog>
        );
    },
);

PriceAvailabilityCheckModal.displayName = 'PriceAvailabilityCheckModal';
